import { ApolloClient, CombinedGraphQLErrors } from '@apollo/client';
import { map } from 'rxjs';

/**
 * ApolloClient that keeps the Apollo Client 3 result contract that the services-px and AI chat
 * callers were written for:
 * - query() and mutate() reject on network errors. With errorPolicy 'all', v4 resolves them as
 *   { data: undefined, error } instead.
 * - GraphQL errors are also exposed as `errors`. v4 only returns `error` (a CombinedGraphQLErrors).
 * - subscribe() reports network errors, and GraphQL errors under errorPolicy 'none', through the
 *   observer's error callback. v4 emits them as { error } and then completes.
 */
export class ApolloClientV3Compat extends ApolloClient {
	constructor(options) {
		super(options);

		// query and mutate are instance properties in v4, so they are wrapped here instead of overridden
		const { query, mutate } = this;
		this.query = async (queryOptions) => toV3Result(await query(queryOptions));
		this.mutate = async (mutateOptions) => toV3Result(await mutate(mutateOptions));
	}

	subscribe(options) {
		const errorPolicy = options.errorPolicy ?? this.defaultOptions.subscribe?.errorPolicy ?? 'none';
		return super.subscribe(options).pipe(map((result) => toV3SubscriptionResult(result, errorPolicy)));
	}
}

const withGraphQLErrors = (result) => ({ ...result, errors: result.error.errors });

const toV3Result = (result) => {
	if (!result.error) return result;
	if (!CombinedGraphQLErrors.is(result.error)) throw result.error;
	return withGraphQLErrors(result);
};

const toV3SubscriptionResult = (result, errorPolicy) => {
	if (!result.error) return result;
	if (!CombinedGraphQLErrors.is(result.error) || errorPolicy === 'none') throw result.error;
	return withGraphQLErrors(result);
};
